/**
 * Which nodes of the graph are named, and where their names go. The graph is quiet at rest: only the nodes that matter
 * wear a name, and a name never lands on another name or on another node. Pure, so it is tested without a browser; the
 * widths are an estimate of a 12 px label (the diagram draws them in screen pixels, outside the zoomed layer).
 */

export const LABEL_CHAR_PX = 6.6;
export const LABEL_HEIGHT_PX = 16;
export const LABEL_GAP_PX = 6;
export const LABEL_MAX_CHARS = 24;
/** Names beyond the ones that were asked for (a selection, a hover) or that mark what needs a person. */
export const LABEL_REST_LIMIT = 12;
/** With this many nodes in view or fewer, every one is named (a scoped graph, or a zoomed one); more, and only the ones that matter. */
export const ALL_NAMED_IN_VIEW = 16;
/** A node is far enough from the edge of the canvas for its name to be read. */
const EDGE_PX = 4;

export interface LabelCandidate {
  id: string;
  /** Screen position of the node's centre, in the canvas's pixels. */
  x: number;
  y: number;
  /** Screen radius of the node. */
  r: number;
  text: string;
  /** 0 is always named (a selection, a hover, a focus); the larger, the sooner it yields. */
  priority: number;
}

export interface PlacedLabel {
  id: string;
  x: number;
  y: number;
  anchor: 'start' | 'end';
  text: string;
  priority: number;
}

interface Box {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export function shortLabel(text: string): string {
  const clean = text.trim();
  return clean.length > LABEL_MAX_CHARS ? `${clean.slice(0, LABEL_MAX_CHARS - 1)}…` : clean;
}

export function labelWidth(text: string): number {
  return Math.ceil(text.length * LABEL_CHAR_PX);
}

const overlaps = (a: Box, b: Box, pad = 2) =>
  a.left < b.right + pad && a.right + pad > b.left && a.top < b.bottom + pad && a.bottom + pad > b.top;

export interface ReservedBox {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/**
 * `reserved` is room something else already covers (the node card sits on the canvas): a name is not put under it, unless it
 * is one that was asked for and has no other place.
 */
export function placeLabels(
  candidates: LabelCandidate[],
  size: { width: number; height: number },
  obstacles: Array<{ id: string; x: number; y: number; r: number }> = [],
  reserved: ReservedBox[] = []
): PlacedLabel[] {
  const placed: PlacedLabel[] = [];
  const taken: Box[] = [...reserved];
  const sorted = candidates
    .map((c, index) => ({ c, index }))
    .sort((a, b) => a.c.priority - b.c.priority || a.index - b.index);
  let optional = 0;

  for (const { c } of sorted) {
    if (c.x < 0 || c.y < 0 || c.x > size.width || c.y > size.height) continue;
    if (c.priority > 0 && optional >= LABEL_REST_LIMIT) continue;
    const text = shortLabel(c.text);
    if (!text) continue;
    const w = labelWidth(text);
    const top = c.y - LABEL_HEIGHT_PX / 2;
    const rights: Box = { left: c.x + c.r + LABEL_GAP_PX, top, right: c.x + c.r + LABEL_GAP_PX + w, bottom: top + LABEL_HEIGHT_PX };
    const lefts: Box = { left: c.x - c.r - LABEL_GAP_PX - w, top, right: c.x - c.r - LABEL_GAP_PX, bottom: top + LABEL_HEIGHT_PX };
    const fits = (box: Box) =>
      box.left >= EDGE_PX &&
      box.right <= size.width - EDGE_PX &&
      box.top >= EDGE_PX &&
      box.bottom <= size.height - EDGE_PX;
    const free = (box: Box) =>
      !taken.some((t) => overlaps(box, t)) &&
      !obstacles.some(
        (o) => o.id !== c.id && overlaps(box, { left: o.x - o.r, top: o.y - o.r, right: o.x + o.r, bottom: o.y + o.r }, 1)
      );

    // Always-named labels (priority 0) take the side that fits even when it is crowded; the others need a free place.
    const options: Array<[Box, 'start' | 'end']> = [
      [rights, 'start'],
      [lefts, 'end'],
    ];
    const choice =
      options.find(([box]) => fits(box) && free(box)) ?? (c.priority === 0 ? options.find(([box]) => fits(box)) : undefined);
    if (!choice) continue;
    const [box, anchor] = choice;
    taken.push(box);
    if (c.priority > 0) optional += 1;
    placed.push({
      id: c.id,
      x: anchor === 'start' ? box.left : box.right,
      y: c.y,
      anchor,
      text,
      priority: c.priority,
    });
  }
  return placed;
}
